import { inject, Injectable, signal } from "@angular/core";
import { ChatApiService } from "@app/shared/chat/services/chat-api.service";
import { map, of, tap } from "rxjs";
import { IComputedConversation, IConversation, IUnresolvedConversation } from "@app/shared/chat/interfaces/conversation.interface";
import { AuthServiceImpl } from "@app/shared/authentication/services/auth.servcice";
import { Router } from "@angular/router";
import { ChatSocketService } from "@app/shared/chat/services/chat-socket.service";
import { IChatMessage } from "@app/shared/chat/interfaces/chat-message.interface";
import { ISwapRequestEntity } from "@app/shared/swap-request/interfaces/swap-request.interface";

@Injectable({
  providedIn: "root",
})
export class ChatService {
  authService = inject(AuthServiceImpl);
  chatApiService = inject(ChatApiService);
  chatSocketService = inject(ChatSocketService);
  router = inject(Router);

  unseenMessages = signal(0);

  conversations = signal<IComputedConversation[]>([]);

  activeSwapRequest = signal<ISwapRequestEntity | undefined>(undefined);

  constructor() {
    this.loadNotSeenMessagesCount().subscribe();
    this.chatSocketService.observerChatMessagesEvent().subscribe({
      next: data => this.handleIncomingMessage(data),
    });
  }

  handleIncomingMessage(data: { conversation: IUnresolvedConversation; message: IChatMessage }) {
    if (data.message.senderSwapRequest === this.activeSwapRequest()?.id) {
      if (this.conversations()?.[0].id === data.conversation._id) {
        return this.updateConversation(data.conversation._id, { newMessagesCount: 0, lastMessage: data.message });
      }
      return;
    }
    this.loadConversation(false).subscribe();
    this.adjustUnseenMessagesCount(1);
  }

  loadConversation(showLoading = true, force = false) {
    if (!force && this.conversations().length) {
      return of(this.conversations());
    }
    return this.chatApiService.getConversations(1, showLoading).pipe(
      tap(result => this.setRawConversations(result.data)),
      map(() => this.conversations())
    );
  }

  setRawConversations(data: IConversation[]) {
    this.conversations.set(this.populateCounterpartSwapRequests(data));
  }

  private populateCounterpartSwapRequests(data: IConversation[]): IComputedConversation[] {
    return data.map((conversation: IConversation) => ({
      ...conversation,
      counterpartSwapRequest: this.computeCounterpartSwapRequest(conversation),
    }));
  }

  computeCounterpartSwapRequest(conversation: IConversation) {
    return conversation.swapRequestA.user?.id === this.authService.getUser()?.id ? conversation.swapRequestB : conversation.swapRequestA;
  }

  updateConversation(id: string, data: Partial<IComputedConversation>) {
    this.conversations.update(list => list.map(c => (c.id === id ? { ...c, ...data } : c)));
  }

  async markConversationAsSeen(conversation: IComputedConversation) {
    if (!conversation.lastMessage || conversation.lastMessage?.isSeen) {
      return;
    }
    this.updateConversation(conversation.id, { ...conversation, newMessagesCount: 0 });
    // Notify the backend,
    await this.chatSocketService.markMessagesAsSeen(conversation.counterpartSwapRequest.id);
    this.adjustUnseenMessagesCount(-1 * conversation.newMessagesCount);
  }

  async openConversation(conversation: IComputedConversation) {
    await this.router.navigate(["/messages", conversation.counterpartSwapRequest.id]);
    if (!conversation.lastMessage?.isSeen) {
      // Mark conversation as seen
      return this.markConversationAsSeen(conversation);
    }
  }

  loadNotSeenMessagesCount() {
    return this.chatApiService.getNotSeenMessagesCount().pipe(
      tap(result => {
        this.setNotSeenMessagesCount(result.data.unseenConversationsCount);
      })
    );
  }

  setNotSeenMessagesCount(count: number) {
    this.unseenMessages.set(count);
  }

  adjustUnseenMessagesCount(amount = 1) {
    if (amount > 0) {
      this.playNewMessageAudio();
    }
    this.setNotSeenMessagesCount(Math.max(0, this.unseenMessages() + amount));
  }

  setActiveSwapRequest(swapRequest?: ISwapRequestEntity) {
    this.activeSwapRequest.set(swapRequest);
  }

  playNewMessageAudio() {
    const audio = new Audio("/audio/new-message.mp3");
    audio.play().catch(e => {
      console.error(e);
    });
  }
}
